import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { NotificationProvider } from './lib/notifications';
import { Toaster } from './components/ui/sonner';
import Layout from './components/layout/Layout';
import LoginPage from './components/pages/LoginPage';
import Dashboard from './components/pages/Dashboard';
import EmailList from './components/pages/EmailList';
import EmailDetailPage from './components/pages/EmailDetail';
import SupplierListPage from './components/pages/SupplierList';
import QuoteListPage from './components/pages/QuoteList';
import ApprovalList from './components/pages/ApprovalList';
import ChatPage from './components/pages/ChatPage';
import SettingsPage from './components/pages/SettingsPage';
import SubmittalGenerator from './components/pages/SubmittalGenerator';
import AIServicesPage from './components/pages/AIServicesPage';
import DraftsPage from './components/pages/DraftsPage';
import PdfModifierPage from './components/pages/PdfModifierPage';
import DesignStudioPage from './components/pages/DesignStudioPage';
import ProfileTemplateGenerator from './components/pages/ProfileTemplateGenerator';
import CadStudioPage from './components/pages/CadStudioPage';
import TemplateStudioPage from './components/pages/TemplateStudioPage';
import QuantityCalculatorPage from './components/pages/QuantityCalculatorPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emails"
        element={
          <ProtectedRoute>
            <EmailList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emails/:id"
        element={
          <ProtectedRoute>
            <EmailDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drafts"
        element={
          <ProtectedRoute>
            <DraftsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            <SupplierListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute>
            <QuoteListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <ProtectedRoute>
            <ApprovalList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/submittals"
        element={
          <ProtectedRoute>
            <SubmittalGenerator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-services"
        element={
          <ProtectedRoute>
            <AIServicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pdf-modifier"
        element={
          <ProtectedRoute>
            <PdfModifierPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/design-studio"
        element={
          <ProtectedRoute>
            <DesignStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile-generator"
        element={
          <ProtectedRoute>
            <ProfileTemplateGenerator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cad-studio"
        element={
          <ProtectedRoute>
            <CadStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/template-studio"
        element={
          <ProtectedRoute>
            <TemplateStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quantity-calculator"
        element={
          <ProtectedRoute>
            <QuantityCalculatorPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
